import React from 'react';
import { Wallet, Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '../../lib/tenant';
import { useSiteContent } from '../../lib/siteContent';

export default function Footer() {
  const { content } = useSiteContent();
  const c = content.contact;

  return (
    <footer className="relative z-10 border-t border-white/[0.06] text-white">
      <div className="border-b border-slate-800/80">
        <div className="container mx-auto px-4 py-12">
          <div className="mx-auto max-w-4xl text-center">
            <h3 className="mb-4 font-display text-2xl font-semibold tracking-tight md:text-3xl">
              Product updates from {PRODUCT_NAME}
            </h3>
            <p className="mb-6 text-slate-400">
              Release notes, compliance templates, and rollout guides—same rhythm as modern SaaS comms.
            </p>
            <form className="mx-auto flex max-w-2xl flex-col gap-4 sm:flex-row" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Work email"
                className="flex-1 rounded-xl border border-slate-700 bg-slate-800/50 px-6 py-3 text-slate-100 placeholder:text-slate-500 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-xl bg-gradient-to-b from-emerald-500 to-emerald-700 px-8 py-3 font-semibold text-white shadow-lg transition-all hover:from-emerald-400 hover:to-emerald-600"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 gap-12 text-center md:grid-cols-2 md:text-left lg:grid-cols-4">
          <div className="flex flex-col items-center space-y-6 md:items-start">
            <div className="flex items-center space-x-2">
              <Wallet className="h-8 w-8 text-emerald-400" />
              <span className="font-display text-2xl font-semibold tracking-tight">{PRODUCT_NAME}</span>
            </div>
            <p className="max-w-xs text-slate-400">
              {PRODUCT_TAGLINE} — multi-tenant financial operations for vehicle finance shops. Register, subscribe, and run
              HQ plus branch ledgers in one workspace.
            </p>
            <div className="flex space-x-3">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, index) => (
                <a
                  key={index}
                  href="#social"
                  className="rounded-xl border border-slate-700 bg-slate-800/60 p-2.5 text-slate-300 transition-colors hover:border-emerald-500/50 hover:bg-slate-800 hover:text-emerald-300"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center md:items-start">
            <h3 className="mb-6 text-sm font-bold uppercase tracking-wider text-emerald-200/90">Product</h3>
            <ul className="space-y-4">
              {[
                { label: 'Register organization', to: '/register' as const },
                { label: 'Pricing', href: '/#pricing' as const },
                { label: 'Sign in', to: '/login' as const },
                { label: 'Dashboard', to: '/dashboard' as const },
              ].map((item) => (
                <li key={item.label}>
                  {'href' in item ? (
                    <a
                      href={item.href}
                      className="group flex items-center text-slate-400 transition-colors hover:text-white"
                    >
                      <ArrowRight className="mr-2 h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                      {item.label}
                    </a>
                  ) : (
                    <NavLink
                      to={item.to}
                      className="group flex items-center text-slate-400 transition-colors hover:text-white"
                    >
                      <ArrowRight className="mr-2 h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                      {item.label}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-start">
            <h3 className="mb-6 text-sm font-bold uppercase tracking-wider text-emerald-200/90">Learn</h3>
            <ul className="space-y-4">
              {[
                { label: 'Features', href: '#features' },
                { label: 'How it works', href: '#flow' },
                { label: 'Customer stories', href: '#stories' },
                { label: 'Contact', href: '#contact' },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="group flex items-center text-slate-400 transition-colors hover:text-white"
                  >
                    <ArrowRight className="mr-2 h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-center md:items-start">
            <h3 className="mb-6 text-sm font-bold uppercase tracking-wider text-emerald-200/90">Contact</h3>
            <ul className="flex flex-col items-center space-y-4 md:items-start">
              {[
                { Icon: Phone, text: c.phone, href: 'tel:+917799001866' },
                { Icon: Mail, text: c.email, href: `mailto:${c.email}` },
                { Icon: MapPin, text: c.address },
              ].map((item, index) => (
                <li key={index} className="flex items-center text-slate-400">
                  <item.Icon className="mr-3 h-5 w-5 shrink-0 text-emerald-500/80" />
                  {'href' in item && item.href && item.text ? (
                    <a href={item.href} className="transition-colors hover:text-emerald-300">
                      {item.text}
                    </a>
                  ) : (
                    <span>{item.text}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80">
        <div className="container mx-auto flex flex-col items-center justify-between gap-4 px-4 py-6 md:flex-row">
          <div className="text-center md:text-left">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} {PRODUCT_NAME}. All rights reserved.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Developed by <span className="font-semibold text-slate-300">Trigovex Technologies Private Limited</span>
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <NavLink to="/privacy-policy" className="text-sm text-slate-500 transition-colors hover:text-emerald-300">
              Privacy
            </NavLink>
            <NavLink to="/delete-account" className="text-sm text-slate-500 transition-colors hover:text-emerald-300">
              Delete account
            </NavLink>
            <a href="#terms" className="text-sm text-slate-500 transition-colors hover:text-emerald-300">
              Terms
            </a>
            <a href="#cookies" className="text-sm text-slate-500 transition-colors hover:text-emerald-300">
              Cookies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
